<template>
    <div role="application" class="auth-shell h-screen flex align-items-center justify-content-center" :style="estiloFundo">
        <!--
            Fundo do tenant: vídeo quando houver, imagem caso contrário. A
            imagem é o `poster` do vídeo, então é ela que aparece enquanto o
            vídeo carrega e se o autoplay for recusado.

            O `@loadeddata` existe porque o autoplay sozinho NÃO é confiável
            aqui, e a falha é silenciosa: sem erro, sem promise rejeitada, só o
            vídeo parado no primeiro quadro. Como o `poster` é um quadro do
            próprio vídeo, a tela continua bonita e ninguém reporta.

            Medido em 2026-09-23, com o vídeo já em cache: em 1920x1080 ficava
            parado, em 900x700 tocava. O Chrome avalia o autoplay quando o
            elemento é criado, e o Vue define `muted` como PROPRIEDADE logo
            depois — nessa janela o vídeo ainda conta como não-mudo e a decisão
            sai negativa, sem ser reavaliada. Tela maior dá mais trabalho de
            layout e desloca esse instante, o que explica a correlação com o
            tamanho sem que o tamanho seja a causa.

            Trocar `muted` por `:muted.attr` foi tentado e PIORA: o atributo
            alimenta `defaultMuted`, e a propriedade `muted` só é inicializada a
            partir dele na criação do elemento. Com o Vue pondo o atributo
            depois, a propriedade fica falsa — e aí nem forçar `play()` resolve.

            O que funciona, medido: com a propriedade verdadeira, `play()`
            sempre inicia. Então mantemos `muted` no template e pedimos a
            reprodução quando o primeiro quadro chega.
        -->
        <video
            v-if="tenant.videoFundo && !menosMovimento"
            class="auth-fundo auth-video"
            :src="tenant.videoFundo"
            :poster="tenant.imagemFundo || undefined"
            autoplay loop muted playsinline
            aria-hidden="true"
            @loadeddata="garantirReproducao"
        ></video>

        <div
            v-else-if="tenant.imagemFundo"
            class="auth-fundo"
            :style="{ backgroundImage: `url('${tenant.imagemFundo}')` }"
            aria-hidden="true"
        ></div>

        <!--
            Véu do mesmo tom do tenant, como o site da marca faz sobre o próprio
            hero: segura o contraste do formulário e impede o fundo de virar o
            assunto da tela.

            Só sobre vídeo. Imagem de fundo é arte parada, feita sob medida para
            ficar atrás de um formulário — o véu por cima dela não resolvia
            contraste nenhum e transformava o degradê da GTS em marrom.
        -->
        <div v-if="tenant.videoFundo && !menosMovimento" class="auth-veu" aria-hidden="true"></div>

        <div class="p-5 w-11 md:w-6 lg:w-3 auth-conteudo">
            <img v-if="tenant.logo" :src="tenant.logo" :alt="tenant.nome" class="auth-logo" />
            <router-view></router-view>
        </div>
    </div>
</template>

<script>
import Tenant from '@/utils/Tenant.js';

export default {
    data() {
        return {
            isDarkMode: true,
            tenant: Tenant,
            // Quem pediu menos animação no sistema cai na imagem parada — que
            // é a mesma arte, sem movimento. Precisa ser aqui e não no CSS:
            // esconder o vídeo por CSS levaria junto o `poster`, e a tela
            // ficaria sem fundo nenhum.
            menosMovimento: typeof window !== 'undefined'
                && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
        };
    },
    computed: {
        /**
         * Cor do tenant como base (T3.3/T3.4). O degradê é derivado dela com
         * `color-mix` em vez de guardar dois tons no banco: um tenant novo
         * informa uma cor só e ganha o mesmo tratamento.
         *
         * A imagem de fundo fica num elemento próprio (`.auth-fundo`), e não
         * aqui: é ele que anima, e animar um elemento é mais barato para o
         * navegador do que animar o `background` do contêiner.
         */
        estiloFundo() {
            const estilo = {
                '--tenant-fundo': this.tenant.corFundo,
                '--tenant-destaque': this.tenant.corDestaque,
            };

            return estilo;
        },
    },
    beforeMount() {
        this.loadThemePreference();
    },
    methods: {
        loadThemePreference() {
            const saved = localStorage.getItem('gts_theme_dark');
            this.isDarkMode = saved !== '0'; // Defaults to dark (true) if not set or not '0'
            this.applyThemeClass();
        },
        applyThemeClass() {
            document.documentElement.classList.toggle('my-app-dark', this.isDarkMode);
            window.dispatchEvent(new Event('theme-changed'));
        },

        /**
         * Reforça a reprodução quando o primeiro quadro chega — ver o comentário
         * no template para o porquê de o autoplay sozinho não bastar.
         *
         * `muted = true` de novo aqui não é redundância: é o que torna a chamada
         * legítima para a política de autoplay, e é barato garantir em vez de
         * depender da ordem em que o Vue aplicou o atributo.
         *
         * O catch é vazio de propósito. Se o navegador ainda assim recusar — por
         * configuração do usuário, por economia de energia — o `poster` já está
         * na tela e é um quadro do próprio vídeo. Degrada para fundo parado, que
         * é exatamente o que quem pediu `prefers-reduced-motion` recebe.
         */
        garantirReproducao(evento) {
            const video = evento.target;

            video.muted = true;
            video.play().catch(() => {});
        }
    }
};
</script>

<style scoped>
.auth-shell {
    position: relative;
    overflow: hidden;
    background-color: var(--tenant-fundo);
    background-image: linear-gradient(
        160deg,
        color-mix(in srgb, var(--tenant-fundo) 92%, white) 0%,
        var(--tenant-fundo) 50%,
        color-mix(in srgb, var(--tenant-fundo) 92%, white) 100%
    );
}

/* Fundo (vídeo ou imagem): decoração, atrás de tudo, sem receber clique. */
.auth-fundo {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    object-fit: cover;
    pointer-events: none;
    z-index: 0;
}

/*
 * Véu sobre o fundo. O site da marca faz o mesmo no hero: a arte é bonita, mas
 * sem isto ela disputa atenção com os campos e o contraste do formulário cai.
 */
.auth-veu {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background: radial-gradient(
            ellipse at center,
            color-mix(in srgb, var(--tenant-fundo) 55%, transparent) 0%,
            color-mix(in srgb, var(--tenant-fundo) 80%, transparent) 70%
        );
}



.auth-conteudo {
    position: relative;
    z-index: 1;
}

.auth-logo {
    display: block;
    max-width: 180px;
    max-height: 64px;
    margin: 0 auto 1.5rem;
    object-fit: contain;
}

/* Autofill do navegador: evita fundo amarelado em tema escuro */
.auth-shell :deep(.p-inputtext:-webkit-autofill),
.auth-shell :deep(.p-password input:-webkit-autofill) {
    -webkit-text-fill-color: #e2e8f0;
    box-shadow: 0 0 0 1000px #334155 inset;
    transition: background-color 99999s ease-out;
}
</style>
